// src/components/BlobGrid.js — generic blob grid: CDN thumbnails, CID copy,
// download. Used by both the Orphans and Blobs held tabs.
//
// Rendering follows atproto.at's blob page: thumbnails come from Bluesky's
// image CDN (https://cdn.bsky.app/img/feed_thumbnail/plain/{did}/{cid}@jpeg),
// no auth, no fetch — a plain <img> with an onError fallback to the generic
// icon for non-image blobs (video, captions, PDFs, …).
//
// Download fetches the blob through the authenticated agent (same-origin blob
// URL so the `download` attribute is honored cross-browser), falling back to
// a direct PDS getBlob navigation if the fetch fails.

import React, { useState, useCallback } from 'react';
import { downloadBlob } from '../lib/backup';

const CID_PREFIX_LEN = 16;

const THUMB_CDN = (did, cid) =>
  `https://cdn.bsky.app/img/feed_thumbnail/plain/${did}/${cid}@jpeg`;

const BlobThumb = ({ did, cid }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <div className="blob-thumb blob-thumb-generic" aria-label="non-image blob" />;
  }

  return (
    <img
      className="blob-thumb"
      src={THUMB_CDN(did, cid)}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const BlobCard = ({ cid, did, pdsUrl, agent, selected, onToggle, copiedCid, onCopy }) => {
  const [downloading, setDownloading] = useState(false);
  const directUrl = `${pdsUrl}/xrpc/com.atproto.sync.getBlob?did=${did}&cid=${cid}`;

  const handleDownload = useCallback(
    async (e) => {
      e.preventDefault();

      if (!agent) {
        window.open(directUrl, '_blank', 'noopener');

        return;
      }

      setDownloading(true);

      try {
        const res = await agent.api.com.atproto.sync.getBlob({ did, cid });
        const data = res?.data;
        let blob;

        if (data instanceof Blob) {
          blob = data;
        } else if (data instanceof ArrayBuffer) {
          blob = new Blob([data]);
        } else if (data && data.byteLength !== undefined && data.byteLength !== null) {
          // Uint8Array (what getBlob actually returns)
          blob = new Blob([data]);
        } else {
          throw new Error('unexpected getBlob response');
        }

        downloadBlob(blob, `${cid}.bin`);
      } catch (err) {
        console.error(`Download failed for ${cid}:`, err);
        window.open(directUrl, '_blank', 'noopener');
      } finally {
        setDownloading(false);
      }
    },
    [agent, did, cid, directUrl]
  );

  return (
    <div className="blob-card">
      <label className="blob-check">
        <input
          type="checkbox"
          checked={selected.has(cid)}
          onChange={() => onToggle(cid)}
          aria-label={`Select blob ${cid}`}
        />
      </label>
      <BlobThumb did={did} cid={cid} />
      <button className="cid-button" onClick={() => onCopy(cid)} title="Click to copy full CID">
        {copiedCid === cid ? '✓ copied' : `${cid.slice(0, CID_PREFIX_LEN)}…`}
      </button>
      <a
        className="download-one"
        href={directUrl}
        download={`${cid}.bin`}
        onClick={handleDownload}
        aria-disabled={downloading}
      >
        {downloading ? 'Downloading…' : 'Download'}
      </a>
    </div>
  );
};

const BlobGrid = ({ cids, pdsUrl, did, agent, selected, onToggleOne, title }) => {
  const [copiedCid, setCopiedCid] = useState(null);

  const copyCid = async (cid) => {
    try {
      await navigator.clipboard.writeText(cid);
      setCopiedCid(cid);
      setTimeout(() => setCopiedCid(null), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  if (cids.length === 0) {
    return (
      <div className="blob-grid-empty">
        <p className="muted">Nothing here.</p>
      </div>
    );
  }

  return (
    <div className="blob-grid-wrap">
      <h3>{title}</h3>
      <div className="blob-grid">
        {cids.map((cid) => (
          <BlobCard
            key={cid}
            cid={cid}
            did={did}
            pdsUrl={pdsUrl}
            agent={agent}
            selected={selected}
            onToggle={onToggleOne}
            copiedCid={copiedCid}
            onCopy={copyCid}
          />
        ))}
      </div>
    </div>
  );
};

export default BlobGrid;
